import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { clientesApi } from "@/lib/clientesApi";
import { toast } from "sonner";
import { formatTelefone, formatDocumento, apenasDigitos } from "@/lib/masks";

const vazio = { tipo: "PF", nome: "", documento: "", contato_nome: "", email: "", telefone: "" };
// O backend responde com código; sem tradução o usuário via "document_conflict".
const MENSAGENS = {
  document_conflict: "Já existe um cliente com esse CPF/CNPJ neste escritório.",
  document_invalid: "CPF/CNPJ inválido. Confira os dígitos.",
  client_type_invalid: "Selecione se o cliente é pessoa física ou jurídica.",
  phase2_disabled: "O cadastro de clientes não está habilitado nesta conta.",
  phase2b_disabled: "O cadastro de clientes não está habilitado nesta conta.",
};
const controle = "w-full h-11 px-3 rounded-lg border border-[#D1DEEE] bg-white text-[#001533]";

function Campo({ label, hint, obrigatorio, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#001533] mb-1">
        {label}{obrigatorio && <span className="text-[#003882]"> *</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-[#335480] mt-1">{hint}</span>}
    </label>
  );
}

export default function NovoClienteDialog({ onClose, onCreated }) {
  const [form, setForm] = useState(vazio);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const pf = form.tipo === "PF";
  const set = (campo) => (event) => setForm({ ...form, [campo]: event.target.value });
  const setMascarado = (campo, mascara) => (event) =>
    setForm({ ...form, [campo]: mascara(event.target.value) });

  const submit = async (event) => {
    event.preventDefault();
    setErro("");
    if (!form.nome.trim()) return setErro(pf ? "Informe o nome do cliente." : "Informe a razão social.");
    setSalvando(true);
    try {
      const cliente = await clientesApi.create({
        expected_version: 0,
        tipo: form.tipo,
        nome: form.nome.trim(),
        documento: apenasDigitos(form.documento) || null,
        document_type: pf ? "cpf" : "cnpj",
      });
      // O contato é opcional, mas é dele que saem e-mail e telefone usados na
      // cobrança; falha aqui não desfaz o cliente já criado.
      if (form.email.trim() || form.telefone.trim()) {
        try {
          await clientesApi.addContact(cliente.cliente_id, {
            expected_version: 0,
            nome: form.contato_nome.trim() || form.nome.trim(),
            email: form.email.trim() || null,
            telefone: apenasDigitos(form.telefone) || null,
          });
        } catch {
          toast.warning("Cliente criado, mas não foi possível salvar o contato. Edite o cliente para incluí-lo.");
        }
      }
      toast.success("Cliente cadastrado");
      onCreated?.(cliente);
      onClose();
    } catch (err) {
      const detalhe = err?.response?.data?.detail;
      setErro(MENSAGENS[detalhe?.code] || detalhe?.message
        || "Não foi possível cadastrar o cliente. Confira os dados.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form data-testid="novo-cliente-form" onSubmit={submit} className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg space-y-3 my-8">
        <h2 className="text-xl font-semibold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>Novo cliente</h2>

        <Campo label="Tipo" obrigatorio>
          <select data-testid="cliente-tipo" className={controle} value={form.tipo} onChange={set("tipo")}>
            <option value="PF">Pessoa física</option>
            <option value="PJ">Pessoa jurídica</option>
          </select>
        </Campo>

        <Campo label={pf ? "Nome completo" : "Razão social"} obrigatorio>
          <Input data-testid="cliente-nome" value={form.nome} onChange={set("nome")} placeholder={pf ? "Ex.: Maria Souza" : "Ex.: Souza Comércio Ltda"} />
        </Campo>

        <Campo label={pf ? "CPF" : "CNPJ"} hint="Necessário para emitir cobrança pelo Asaas. Fica cifrado e mascarado na tela.">
          <Input data-testid="cliente-documento" value={form.documento} onChange={setMascarado("documento", (v) => formatDocumento(v, form.tipo))} placeholder={pf ? "000.000.000-00" : "00.000.000/0000-00"} inputMode="numeric" />
        </Campo>

        <div className="pt-2 border-t border-[#D1DEEE]">
          <p className="text-sm font-medium text-[#001533] mt-3 mb-2">Contato</p>
          <div className="space-y-3">
            <Campo label="Nome do contato" hint="Deixe vazio para usar o nome do cliente">
              <Input data-testid="cliente-contato-nome" value={form.contato_nome} onChange={set("contato_nome")} />
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="E-mail" hint="Recebe a cobrança">
                <Input data-testid="cliente-email" type="email" value={form.email} onChange={set("email")} placeholder="cliente@exemplo.com" />
              </Campo>
              <Campo label="Telefone">
                <Input data-testid="cliente-telefone" value={form.telefone} onChange={setMascarado("telefone", formatTelefone)} placeholder="(11) 90000-0000" inputMode="tel" />
              </Campo>
            </div>
          </div>
        </div>

        {erro && <p role="alert" className="text-red-700 text-sm">{erro}</p>}
        <div className="flex gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" data-testid="salvar-cliente" disabled={salvando}>{salvando ? "Salvando..." : "Cadastrar cliente"}</Button>
        </div>
      </div>
    </form>
  );
}
